import { Show, TextField, ReferenceField, DateField, FunctionField, Labeled } from 'react-admin';
import { Box } from '@mui/material';
import { ChipList } from '../common';
import { DATETIME_FORMAT } from '../../utils';
import { Event, EventType, Machine, Printing } from '../../types';
import { EventPhoto } from './EventPhoto';
import { PrintStopCause } from './PrintStopCause';
import { PelletDispenseInfo } from './PelletDispenseInfo';
import { WizardAction } from './WizardAction';
import { MaterialChanged } from './MaterialChanged';
import { BedMeshCalibration } from './BedMeshCalibration';
import { NeedBedCalibration } from './NeedBedCalibration';
import { PrintPhoto } from './PrintPhoto';

export const EventShow = () => {
  return (
    <Show>
      <FunctionField
        render={(record: Event) =>
          record.photoUrl ? (
            <Box sx={{ mb: 3, mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Labeled label="Print Photo" sx={{ '& .RaLabeled-label': { fontSize: '1.2rem' } }}>
                <EventPhoto photoUrl={record.photoUrl} size="full" />
              </Labeled>
            </Box>
          ) : null
        }
      />

      <Box
        sx={{
          p: 2,
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
          },
          gap: 2,
        }}
      >
        <Labeled label="ID">
          <TextField source="id" />
        </Labeled>

        <Labeled label="Event Type">
          <ReferenceField source="eventTypeId" reference="event-types" link={false}>
            <FunctionField
              render={(eventType: EventType) =>
                eventType ? (
                  <ChipList
                    items={[
                      {
                        id: eventType.id,
                        label: eventType.name,
                        color: 'primary',
                      },
                    ]}
                  />
                ) : null
              }
            />
          </ReferenceField>
        </Labeled>

        <Labeled label="Event Time">
          <DateField source="eventTime" showTime options={DATETIME_FORMAT} />
        </Labeled>

        <Labeled label="Machine">
          <ReferenceField source="machineId" reference="machines" link={false}>
            <FunctionField
              render={(machine: Machine) =>
                machine ? (
                  <ChipList
                    items={[
                      {
                        id: machine.id,
                        label: machine.serial,
                        to: `/machines/${machine.id}/show`,
                      },
                    ]}
                  />
                ) : (
                  <ChipList items={[]} emptyText="N/A" />
                )
              }
            />
          </ReferenceField>
        </Labeled>

        <Labeled label="Printing">
          <ReferenceField source="printingId" reference="printings" link={false}>
            <FunctionField
              render={(printing: Printing) =>
                printing ? (
                  <ChipList
                    items={[
                      {
                        id: printing.id,
                        label: printing.id.toString(),
                        to: `/printings/${printing.id}/show`,
                      },
                    ]}
                  />
                ) : (
                  <ChipList items={[]} emptyText="N/A" />
                )
              }
            />
          </ReferenceField>
        </Labeled>

        <Labeled label="Order ID">
          <ReferenceField source="printingId" reference="printings" link={false}>
            <TextField source="orderId" emptyText="N/A" />
          </ReferenceField>
        </Labeled>

        <Labeled label="Timestamp">
          <TextField source="ts" />
        </Labeled>

        <Labeled label="Error">
          <TextField source="error" emptyText="N/A" />
        </Labeled>

        <Labeled label="Created At">
          <DateField source="createdAt" showTime options={DATETIME_FORMAT} />
        </Labeled>

        <Labeled label="Updated At">
          <DateField source="updatedAt" showTime options={DATETIME_FORMAT} />
        </Labeled>

        <WizardAction />
      </Box>
      <PrintStopCause />
      <PelletDispenseInfo />
      <PrintPhoto />
      <MaterialChanged />
      <BedMeshCalibration />
      <NeedBedCalibration />
    </Show>
  );
};
